/**
 * Shared widget chrome: icon + title in header, edit (pencil) and remove (x) inside the tile.
 * Used by all dashboard widgets for consistent UX (reference: other project).
 */
import { Pencil, X } from 'lucide-react';
import { getStatusIcon } from '../../lib/statusIcons';

interface DashboardWidgetChromeProps {
  title: string;
  iconName?: string;
  onEdit: () => void;
  onRemove: () => void;
  children: React.ReactNode;
  /** When true (e.g. fullscreen), hide edit/remove so the tile is display-only */
  hideActions?: boolean;
}

export default function DashboardWidgetChrome({
  title,
  iconName,
  onEdit,
  onRemove,
  children,
  hideActions,
}: DashboardWidgetChromeProps) {
  const IconComponent = getStatusIcon(iconName);

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--widget-bg)',
        borderRadius: 8,
        border: '1px solid var(--header-border)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '0.5rem 0.75rem',
          borderBottom: '1px solid var(--header-border)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flexShrink: 0,
        }}
      >
        <IconComponent size={18} strokeWidth={2} style={{ color: 'var(--accent)', flexShrink: 0 }} />
        <span style={{ fontWeight: 600, fontSize: '0.875rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </span>
        {!hideActions && (
          <>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              aria-label="Edit widget"
              style={{
                padding: 4,
                border: 'none',
                background: 'none',
                borderRadius: 4,
                color: 'var(--text-muted)',
                cursor: 'pointer',
                display: 'flex',
              }}
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              aria-label="Remove widget"
              style={{
                padding: 4,
                border: 'none',
                background: 'none',
                borderRadius: 4,
                color: 'var(--text-muted)',
                cursor: 'pointer',
                display: 'flex',
              }}
            >
              <X size={14} />
            </button>
          </>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', containerType: 'size', containerName: 'widget-content' }}>
        {children}
      </div>
    </div>
  );
}
